import { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";
import PageHeading from "../PageHeading";
import DynamicSelect from "../DynamicSelect";
import { useAuth } from "../../utils/AuthContext";
import { PrintBillLayout } from "../PrintBillLayout";
import axios from "axios";
import CreatableSelect from "react-select/creatable";
import HospitalServiceModal from "./HospitalConfig/HospitalServiceList/HospitalServiceModal";
import HospitalAgentModal from "./HospitalConfig/HospitalAgent/HospitalAgentModal";
import HospitalBedModal from "./HospitalConfig/HospitalBed/HospitalBedModal";

const api = axios.create({
  baseURL: "https://api.muktihospital.com/api/hospital",
  headers: {
    "x-api-key":
      "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
  },
});

const GENERAL_API_URL = "https://api.muktihospital.com/api";
const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const HospitalAdmission = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const editAdmissionData = location.state?.editAdmissionData || null;
  const isEditMode = !!editAdmissionData;

  const isAdmin = user?.role === "admin";
  const isSuperAdmin = user?.role === "superadmin";
  const canEditDate = isAdmin || isSuperAdmin;

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Hospital Office" },
    { label: isEditMode ? "Edit Admission" : "Admission" },
  ];

  const parseDate = (dateInput) => {
    if (!dateInput) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    try {
      const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
      
      if (isNaN(date.getTime())) {
         const today = new Date();
         return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch {
      console.warn("Invalid date format:", dateInput);
      return new Date().toISOString().split("T")[0];
    }
  };

  const [formData, setFormData] = useState({
    id: editAdmissionData?.id || "",
    serviceType: editAdmissionData?.serviceType || "",
    patient: editAdmissionData?.patientName || "",
    fathersName: editAdmissionData?.fatherName || "",
    mothersName: editAdmissionData?.motherName || "",
    address: editAdmissionData?.address || "",
    relativeName: editAdmissionData?.relativeName || "",
    co: editAdmissionData?.careOf || "",
    age: editAdmissionData?.age || "",
    sex: editAdmissionData?.sex || "",
    religion: editAdmissionData?.religion || "",
    dateOfBirth: parseDate(editAdmissionData?.dateOfBirth),
    phoneMobile: editAdmissionData?.phoneNumber || "",
    admissionDate: editAdmissionData?.admissionDate
      ? parseDate(editAdmissionData.admissionDate)
      : parseDate(new Date()),
    admissionTime: editAdmissionData?.admissionTime
      ? editAdmissionData.admissionTime.replace(/\+06$/, "")
      : new Date().toLocaleTimeString("en-US", {
          hour12: true,
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "Asia/Dhaka",
        }),
    relativeMobileNo: editAdmissionData?.relativeMobileNumber || "",
    relationship: editAdmissionData?.relationship || "",
    consultantCode: editAdmissionData?.consultantCode || "",
    consultantName: editAdmissionData?.consultantName || "",
    agent: editAdmissionData?.agentName || "",
    agentName: editAdmissionData?.agentName || "",
    bedNo: editAdmissionData?.bedNo || "",
    employeeCode: editAdmissionData?.employeeCode || user?.id || "1001",
    employeeName:
      editAdmissionData?.employeeName || user?.name || "Md Taiful Islam",
    dischargeCode: editAdmissionData?.dischargeCode || "",
    billingType: editAdmissionData?.billingType || "Normal",
    status: editAdmissionData?.status || "Admitted",
    isDateChangeRequested: editAdmissionData?.isDateChangeRequested || false,
  });

  const [selectedService, setSelectedService] = useState(null);
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedBed, setSelectedBed] = useState(null);
  const [selectedBillingType, setSelectedBillingType] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isNewServiceModalOpen, setIsNewServiceModalOpen] = useState(false);
  const [isNewAgentModalOpen, setIsNewAgentModalOpen] = useState(false);
  const [isNewBedModalOpen, setIsNewBedModalOpen] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [patientOptions, setPatientOptions] = useState([]);
  const [selectedPatientOption, setSelectedPatientOption] = useState(null);

  // Data States
  const [serviceOptions, setServiceOptions] = useState([]);
  const [consultantOptions, setConsultantOptions] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);
  const [bedOptions, setBedOptions] = useState([]);
  const [bedData, setBedData] = useState([]);
  const [allBedOptions, setAllBedOptions] = useState([]);
  const [occupiedBeds, setOccupiedBeds] = useState([]);

  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printData, setPrintData] = useState(null);

  const billingTypeOptions = [
    { value: "Normal", label: "Normal" },
    { value: "Insurance", label: "Insurance" },
    { value: "Government", label: "Government" },
  ];

  const statusOptions = [
    { value: "Admitted", label: "Admitted" },
    { value: "Released", label: "Released" },
  ];

  const fetchData = async (
    endpoint,
    setOptions,
    mapResponse,
    additionalState = null
  ) => {
    try {
      const response = await api.get(`/${endpoint}`);
      const data = Array.isArray(response.data)
        ? response.data
        : response.data[endpoint + "s"] ||
          response.data[endpoint] ||
          response.data.data ||
          response.data.results ||
          response.data.items ||
          [];
      if (!Array.isArray(data)) {
        console.error(
          `Invalid ${endpoint} response: Expected array, got`,
          data
        );
        setOptions([]);
        return;
      }
      const options = data.map((item, index) => {
        const mapped = mapResponse(item);
        return mapped;
      });
      setOptions(options);
      if (additionalState) additionalState(data);
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
      setOptions([]);
    }
  };

  useEffect(() => {
    setIsLoading(true);

    const hospitalDataPromise = Promise.all([
      fetchData("service", setServiceOptions, (service) => ({
        value: service.id || service.serviceId || service.code,
        label: service.serviceName || service.name,
      })),
      fetchData("agent", setAgentOptions, (agent) => ({
        value: agent.agentCode || agent.id || agent.code,
        label: agent.agentName || agent.name,
      })),
      fetchData(
        "bed",
        setAllBedOptions,
        (bed) => ({
          value: bed.bedNumber || bed.BED_NUMBER || bed.number,
          label: bed.bedNumber || bed.BED_NUMBER || bed.number,
        }),
        setBedData
      ),
      api.get("/admission/occupied-beds")
        .then((res) => {
          setOccupiedBeds(res.data.occupiedBedIds || []);
        })
        .catch((err) => console.error("Error fetching occupied beds", err)),
    ]);

    const doctorPromise = axios
          .get(`${GENERAL_API_URL}/doctor`, {
            headers: { "x-api-key": API_KEY },
          })
          .then((res) => {
            const data = Array.isArray(res.data) ? res.data : res.data.data || [];
            const options = data.map((d) => {
              let name = d.name || "Unknown";
              if (d.translations) {
                try {
                  const t =
                    typeof d.translations === "string"
                      ? JSON.parse(d.translations)
                      : d.translations;
                  name = t?.en?.name || t?.bn?.name || name;
                } catch (e) {}
              }
              return { value: d.id, label: name };
            });
            setConsultantOptions(options);
          })
          .catch((err) => {
            console.error("Error fetching doctors:", err);
            toast.error("Failed to load doctor list.");
          });

    // 2. Fetch Patients from General API (Fix for 404 Error)
    const patientDataPromise = axios.get(`${GENERAL_API_URL}/patient`, {
        headers: { "x-api-key": API_KEY }
    }).then(response => {
        const formattedPatients = response.data.map((patient) => ({
            value: patient.id,
            label: `${patient.name} (${patient.phoneNumber || "No Phone"})`,
            nameOnly: patient.name,
            phone: patient.phoneNumber || "",
        }));
        setPatientOptions(formattedPatients);
    }).catch(error => {
        console.error("Error fetching patients:", error);
        toast.error("Failed to load patient list.");
    });

    Promise.all([hospitalDataPromise, doctorPromise, patientDataPromise]).finally(() => setIsLoading(false));
  }, []);

  // Filter Beds (Occupied Logic)
  useEffect(() => {
    if (allBedOptions.length > 0) {
      const filteredBeds = allBedOptions.filter((bed) => {
        const isOccupied = occupiedBeds.includes(bed.value);
        if (!isOccupied) return true;
        if (isEditMode && formData.bedNo === bed.value) return true;
        return false;
      });
      setBedOptions(filteredBeds);
    }
  }, [allBedOptions, occupiedBeds, isEditMode, formData.bedNo]);

  useEffect(() => {
    if (
      isEditMode &&
      serviceOptions.length &&
      consultantOptions.length &&
      agentOptions.length &&
      bedOptions.length
    ) {
      const serviceMatch = serviceOptions.find((opt) => opt.value === formData.serviceType);
      setSelectedService(serviceMatch || null);

      const patientMatch = patientOptions.find(p => p.nameOnly === formData.patient) 
                           || { value: 'manual', label: formData.patient };
      setSelectedPatientOption(patientMatch);

      let consultantMatch = consultantOptions.find((opt) => opt.label === formData.consultantName);
      if (!consultantMatch && formData.consultantName) {
        consultantMatch = {
          value: formData.consultantName,
          label: formData.consultantName,
        };
        setConsultantOptions((prev) => [...prev, consultantMatch]);
      }
      setSelectedConsultant(consultantMatch || null);

      const agentMatch = agentOptions.find((opt) => opt.label === formData.agentName);
      setSelectedAgent(agentMatch || null);

      const bedMatch = bedOptions.find((opt) => opt.value === formData.bedNo);
      setSelectedBed(bedMatch || null);

      setSelectedBillingType(
        billingTypeOptions.find((opt) => opt.value === formData.billingType) || null
      );
      setSelectedStatus(
        statusOptions.find((opt) => opt.value === formData.status) || null
      );
    } else {
      setSelectedBillingType(billingTypeOptions.find((opt) => opt.value === "Normal") || null);
      setSelectedStatus(statusOptions.find((opt) => opt.value === "Admitted") || null);
    }
  }, [isEditMode, serviceOptions, consultantOptions, agentOptions, bedOptions, formData, patientOptions]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSelectChange = (field, option) => {
    setFormData((prevData) => {
      let newData = { ...prevData, [field]: option?.value || "" };

      // Auto-fill Ward/Cabin based on Bed Number
      if (field === "bedNo" && option) {
        const selectedBedData = bedData.find(
          (bed) => bed.bedNumber === option.value
        );
        newData.wardCabin = selectedBedData?.wcCode || "";
      }

      if (field === "consultantCode" && option) {
        newData.consultantCode = option.value;
        newData.consultantName = option.label;
      }
      if (field === "agent" && option) {
        newData.agent = option.value;
        newData.agentName = option.label;
      }
      return newData;
    });

    const setters = {
      serviceType: setSelectedService,
      consultantCode: setSelectedConsultant,
      agent: setSelectedAgent,
      bedNo: setSelectedBed,
      billingType: setSelectedBillingType,
      status: setSelectedStatus,
    };
    setters[field](option);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoadingAction('save');
    try {
      const requiredFields = [
        "serviceType",
        "patient",
        "phoneMobile",
        "consultantName",
        "agentName",
        "bedNo",
      ];
      const missingFields = requiredFields.filter(
        (field) => !formData[field] || formData[field] === ""
      );
      if (missingFields.length > 0) {
        toast.error(`Missing required fields: ${missingFields.join(", ")}`, {
          position: "top-right",
          autoClose: 5000,
        });
        setIsLoading(false);
        return;
      }

      const payload = {
        ...formData,
        
        // Manual Mapping
        patientName: formData.patient,
        phoneNumber: formData.phoneMobile,
        fatherName: formData.fathersName,
        motherName: formData.mothersName,
        careOf: formData.co,
        dateOfBirth: formData.dateOfBirth || null,
        admissionDate: formData.admissionDate || null,
      };

      if (!isEditMode) {
        delete payload.id;
      }

      if (isEditMode) {
        await api.put(`/admission/edit/${formData.id}`, payload);
        toast.success(`Admission updated successfully`);
        navigate(".", { replace: true, state: null });
        resetForm(); 
      } else {
        await api.post("/admission/add", payload);
        toast.success(`Admission saved successfully`);
        resetForm();
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "saving"} admission:`,
        error
      );
      toast.error(
        `Failed to ${isEditMode ? "update" : "save"} admission: ${
          error.response?.data?.error || error.message
        }`,
        { position: "top-right", autoClose: 5000 }
      );
    } finally {
      setIsLoading(false);
      setLoadingAction(null);
    }
  };

  const handleSaveAndPrint = async () => {
    setIsLoading(true);
    setLoadingAction('print');
    try {
      const requiredFields = [
        "serviceType",
        "patient",
        "phoneMobile",
        "consultantName",
        "agentName",
        "bedNo",
      ];
      const missingFields = requiredFields.filter(
        (field) => !formData[field] || formData[field] === ""
      );
      if (missingFields.length > 0) {
        toast.error(`Missing required fields: ${missingFields.join(", ")}`, {
          position: "top-right",
          autoClose: 5000,
        });
        setIsLoading(false);
        return;
      }

      const payload = {
        ...formData,
        patientName: formData.patient,
        phoneNumber: formData.phoneMobile,
        fatherName: formData.fathersName,
        motherName: formData.mothersName,
        careOf: formData.co,
        dateOfBirth: formData.dateOfBirth || null,
        admissionDate: formData.admissionDate || null,
      };

      let response;
      if (isEditMode) {
        response = await api.put(`/admission/edit/${formData.id}`, payload);
      } else {
        response = await api.post("/admission/add", payload);
      }

      const billInfo = (
          <div className="w-full font-sans text-gray-800 p-2">
            
            {/* --- TOP HEADER --- */}
            <div className="flex justify-between items-end border-b-2 border-gray-800 pb-3 mb-6">
              <div className="flex-1 pr-4">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Patient Name</p>
                <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 uppercase leading-none break-words">
                  {formData.patient || "Unknown Patient"}
                </h2>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Registration No</p>
                <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 leading-none">
                  {response.data.admission?.regNo || "N/A"}
                </h2>
              </div>
            </div>

            {/* --- MAIN CONTENT GRID --- */}
            <div className="grid grid-cols-3 gap-8 text-[13px] leading-relaxed">
              
              {/* COLUMN 1: PATIENT PROFILE */}
              <div className="col-span-1">
                <h3 className="text-[11px] font-bold text-blue-800 uppercase border-b border-gray-300 pb-1 mb-3 tracking-wider">
                  Patient Profile
                </h3>
                <div className="space-y-2">
                  <div className="flex items-start">
                    <span className="w-20 shrink-0 text-gray-500 font-semibold whitespace-nowrap">Age / Sex:</span>
                    <span className="font-bold">{formData.age || "--"} / {formData.sex || "--"}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="w-20 shrink-0 text-gray-500 font-semibold whitespace-nowrap">DOB:</span>
                    <span className="font-bold">
                      {formData.dateOfBirth ? new Date(formData.dateOfBirth).toLocaleDateString("en-GB") : "--"}
                    </span>
                  </div>
                  <div className="flex items-start">
                    <span className="w-20 shrink-0 text-gray-500 font-semibold whitespace-nowrap">Religion:</span>
                    <span className="font-bold">{formData.religion || "--"}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="w-20 shrink-0 text-gray-500 font-semibold whitespace-nowrap">Mobile:</span>
                    <span className="font-bold">{formData.phoneMobile || "--"}</span>
                  </div>
                </div>
              </div>

              {/* COLUMN 2: GUARDIAN & CONTACT */}
              <div className="col-span-1 border-l border-gray-200 pl-6">
                <h3 className="text-[11px] font-bold text-blue-800 uppercase border-b border-gray-300 pb-1 mb-3 tracking-wider">
                  Guardian / Contact
                </h3>
                <div className="space-y-2">
                  <div className="flex items-start">
                    <span className="w-36 shrink-0 text-gray-500 font-semibold whitespace-nowrap">Father / Husband: </span>
                    <span className="font-bold break-words">{formData.fathersName || "--"}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="w-36 shrink-0 text-gray-500 font-semibold whitespace-nowrap">Mother:</span>
                    <span className="font-bold break-words">{formData.mothersName || "--"}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="w-36 shrink-0 text-gray-500 font-semibold whitespace-nowrap">Guardian:</span>
                    <span className="font-bold break-words">{formData.relativeName || "--"}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="w-36 shrink-0 text-gray-500 font-semibold whitespace-nowrap">Relation:</span>
                    <span className="font-bold">{formData.relationship || "--"}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="w-36 shrink-0 text-gray-500 font-semibold whitespace-nowrap">Contact:</span>
                    <span className="font-bold">{formData.relativeMobileNo || "--"}</span>
                  </div>
                </div>
              </div>

              {/* COLUMN 3: ADMISSION DETAILS */}
              <div className="col-span-1 border-l border-gray-200 pl-6">
                <h3 className="text-[11px] font-bold text-blue-800 uppercase border-b border-gray-300 pb-1 mb-3 tracking-wider">
                  Admission Details
                </h3>
                <div className="space-y-2">
                  {/* Fixed Label Width to prevent wrapping */}
                  <div className="flex items-start">
                    <span className="w-[85px] shrink-0 text-gray-500 font-semibold whitespace-nowrap">Admit Date:</span>
                    <span className="font-bold">{formData.admissionDate || "--"}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="w-[85px] shrink-0 text-gray-500 font-semibold whitespace-nowrap">Time:</span>
                    <span className="font-bold">{formData.admissionTime || "--"}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="w-[85px] shrink-0 text-gray-500 font-semibold whitespace-nowrap">Bed No:</span>
                    <span className="font-bold bg-gray-100 px-2 rounded-sm text-xs border border-gray-300">
                      {selectedBed?.label || "None"}
                    </span>
                  </div>
                  <div className="flex items-start">
                    <span className="w-[85px] shrink-0 text-gray-500 font-semibold whitespace-nowrap">Status:</span>
                    <span className="font-bold">{formData.status || "--"}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="w-[85px] shrink-0 text-gray-500 font-semibold whitespace-nowrap">Bill Type:</span>
                    <span className="font-bold">{formData.billingType || "Normal"}</span>
                  </div>
                </div>
              </div>

            </div>

            {/* --- BOTTOM SECTION: ADDRESS & DOCTORS --- */}
            <div className="mt-8 pt-4 border-t-2 border-gray-100 grid grid-cols-2 gap-10 text-[13px]">
              <div>
                  <span className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Full Address</span>
                  <p className="font-semibold text-gray-900 leading-snug break-words">
                    {formData.address || "N/A"}
                    {formData.co ? ` (C/O: ${formData.co})` : ""}
                  </p>
              </div>
              <div className="space-y-2">
                  <div className="flex items-start">
                    <span className="w-24 shrink-0 text-gray-500 font-semibold whitespace-nowrap">Consultant:</span>
                    <span className="font-bold text-gray-900">{formData.consultantName || "N/A"}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="w-24 shrink-0 text-gray-500 font-semibold whitespace-nowrap">Ref / Agent:</span>
                    <span className="font-bold text-gray-900">{formData.agentName || "N/A"}</span>
                  </div>
              </div>
            </div>
            
            {/* --- ADMIN METADATA--------*/}
            <div className="mt-8 pt-2 border-t border-dashed border-gray-300 flex justify-between text-[10px] text-gray-400 font-mono">
              <span>Entry By: {formData.employeeName} ({formData.employeeCode})</span>
              <span>Printed: {new Date().toLocaleString()}</span>
            </div>

          </div>
        );

      const newPrintData = { billInfo };
      setPrintData(newPrintData);
      setShowPrintPreview(true);
      toast.success(
        `${
          isEditMode ? "Admission updated" : "Admission saved"
        } and print preview triggered!`,
        { position: "top-right", autoClose: 5000 }
      );
      setTimeout(() => {
        window.print();
        setShowPrintPreview(false);
        if (isEditMode) {
            navigate(".", { replace: true, state: null });
            resetForm();
        } else {
            resetForm();
        }
      }, 1500);
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "saving"} and printing admission:`,
        error
      );
      toast.error(
        `Failed to ${isEditMode ? "update" : "save"} and print admission: ${
          error.response?.data?.error || error.message
        }`,
        { position: "top-right", autoClose: 5000 }
      );
    } finally {
      setIsLoading(false);
      setLoadingAction(null);
    }
  };


  const resetForm = () => {
    setFormData({
      id: "",
      serviceType: "",
      patient: "",
      fathersName: "",
      mothersName: "",
      address: "",
      relativeName: "",
      co: "",
      age: "",
      sex: "",
      religion: "",
      dateOfBirth: "",
      phoneMobile: "",
      admissionDate: new Date().toLocaleDateString("en-GB"),
      admissionTime:
        new Date().toLocaleTimeString("en-US", {
          hour12: true,
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "Asia/Dhaka",
        }) + " +06",
      relativeMobileNo: "",
      relationship: "",
      consultantCode: "",
      consultantName: "",
      agent: "",
      agentName: "",
      bedNo: "",
      employeeCode: user?.id || "1001",
      employeeName: user?.name || "Md Taiful Islam",
      dischargeCode: "",
      billingType: "Normal",
      status: "Admitted",
      isDateChangeRequested: false,
    });
    setSelectedService(null);
    setSelectedConsultant(null);
    setSelectedAgent(null);
    setSelectedBed(null);
    setSelectedBillingType(null);
    setSelectedStatus(null);
    setSelectedPatientOption(null);
    setShowPrintPreview(false);
    setPrintData(null);
  };

  const onClose = () => {
    resetForm();
    navigate("/hospital-patients");
  };

  const handleAddNewService = () => setIsNewServiceModalOpen(true);
  const handleAddNewAgent = () => setIsNewAgentModalOpen(true);
  const handleAddNewBed = () => setIsNewBedModalOpen(true);

  const handlePatientSelectChange = (selectedOption) => {
    setSelectedPatientOption(selectedOption);
    
    if (selectedOption) {
      setFormData((prev) => ({
        ...prev,
        patient: selectedOption.nameOnly || selectedOption.label,
        phoneMobile: selectedOption.phone || prev.phoneMobile,
        address: selectedOption.address || prev.address,
        age: selectedOption.age || prev.age,
        sex: selectedOption.sex || prev.sex
      }));
    } else {
       setFormData((prev) => ({ ...prev, patient: "" }));
    }
  };

  const handleCreatePatient = (inputValue) => {
    const newOption = {
        value: `new_${Date.now()}`,
        label: inputValue,
        nameOnly: inputValue,
        phone: ""
    };
    
    setSelectedPatientOption(newOption);
    setPatientOptions((prev) => [...prev, newOption]);
    
    setFormData((prev) => ({
        ...prev,
        patient: inputValue,
        phoneMobile: "", // Reset phone for new entry
    }));
    
    toast.info(`New patient "${inputValue}" added. Please fill in details.`);
  };

  const handleSaveNewService = (savedService, isEditMode) => {
    if (!isEditMode) {
      const newService = {
        value: savedService.id,
        label: savedService.serviceName,
      };
      setServiceOptions((prev) => [...prev, newService]);
      setFormData((prevData) => ({
        ...prevData,
        serviceType: newService.value,
      }));
      setSelectedService(newService);
      toast.success("Service added and selected!", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    setIsNewServiceModalOpen(false);
  };

  const handleSaveNewAgent = (savedAgent, isEditMode) => {
    if (!isEditMode) {
      const newAgent = {
        value: savedAgent.agentCode || savedAgent.id || savedAgent.code,
        label: savedAgent.agentName || savedAgent.name,
      };
      setAgentOptions((prev) => [...prev, newAgent]);
      setFormData((prevData) => ({
        ...prevData,
        agent: newAgent.value,
        agentName: newAgent.label,
      }));
      setSelectedAgent(newAgent);
      toast.success("Agent added and selected!", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    setIsNewAgentModalOpen(false);
  };

  const handleSaveNewBed = (savedBed, isEditMode) => {
    if (!isEditMode) {
      const newBed = {
        value: savedBed.id,
        label: savedBed.bedNumber || savedBed.bedNo || savedBed.number,
      };
      setAllBedOptions((prev) => [...prev, newBed]);
      setBedOptions((prev) => [...prev, newBed]);
      setBedData((prev) => [...prev, savedBed]);
      setFormData((prevData) => ({ ...prevData, bedNo: newBed.value }));
      setSelectedBed(newBed);
      toast.success("Bed added and selected!", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    setIsNewBedModalOpen(false);
  };

  return (
    <div>
      <ToastContainer />
      <PageHeading
        title={isEditMode ? "Edit Admission" : "Admission"}
        breadcrumbs={breadcrumbs}
      />
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <div className="bg-white shadow-lg rounded-lg">
            <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Registration Form
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
              {/* Service Type */}
              <div>
                <label htmlFor="serviceType" className="label">
                  Service Type: <span className="text-red-600">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <DynamicSelect
                      options={serviceOptions}
                      value={selectedService}
                      onChange={(option) =>
                        handleSelectChange("serviceType", option)
                      }
                      isSearchable
                      placeholder="Search Service Type"
                      isDisabled={isLoading}
                      required
                    />
                  </div>
                  <button
                    onClick={handleAddNewService}
                    className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0 text-sm"
                    disabled={isLoading}
                  >
                    Add New
                  </button>
                </div>
              </div>

              {/* Patient Name */}
            <div>
                <label className="label">
                  Patient's Name: <span className="text-red-600">*</span>
                </label>
                <CreatableSelect
                  isClearable
                  isSearchable
                  options={patientOptions}
                  value={selectedPatientOption}
                  onChange={handlePatientSelectChange}
                  onCreateOption={handleCreatePatient}
                  placeholder="Select or Type New Patient Name"
                  formatCreateLabel={(inputValue) => `Add new patient: "${inputValue}"`}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: '#e2e8f0',
                        fontSize: '14px',
                        backgroundColor: '#f7fafc',
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        fontSize: '14px',
                        backgroundColor: state.isSelected
                          ? '#3b82f6'
                          : state.isFocused
                          ? '#ebf8ff'
                          : 'transparent',
                        color: state.isSelected ? '#ffffff' : '#2d3748',
                      }),
                      menu: (provided) => ({
                        ...provided,
                        backgroundColor: '#ffffff',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        padding: '0px',
                      }),
                    }}
                />
                {/* Hidden input to ensure HTML5 'required' validation works */}
                <input 
                  tabIndex={-1}
                  autoComplete="off"
                  style={{ opacity: 0, height: 0, position: 'absolute' }}
                  value={formData.patient}
                  required
                  onChange={()=>{}}
                />
              </div>

              {/* Standard Inputs */}
              <div>
                <label className="label">Father's / Husband's Name:</label>  
                <input
                  type="text"
                  name="fathersName"
                  value={formData.fathersName}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="mothersName" className="label">
                  Mother's Name:
                </label>
                <input
                  type="text"
                  id="mothersName"
                  name="mothersName"
                  value={formData.mothersName}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="relativeName" className="label">
                  Relative Name:
                </label>
                <input
                  type="text"
                  id="relativeName"
                  name="relativeName"
                  value={formData.relativeName}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="co" className="label">
                  C/O:
                </label>
                <input
                  type="text"
                  id="co"
                  name="co"
                  value={formData.co}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="age" className="label">
                  Age:
                </label>
                <input
                  type="text"
                  id="age"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="sex" className="label">
                  Sex:
                </label>
                <input
                  type="text"
                  id="sex"
                  name="sex"
                  value={formData.sex}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="religion" className="label">
                  Religion:
                </label>
                <input
                  type="text"
                  id="religion"
                  name="religion"
                  value={formData.religion}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="address" className="label">
                  Address:
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="dateOfBirth" className="label">
                  Date of Birth:
                </label>
                <input
                  type="date"
                  id="dateOfBirth"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>

              <div>
                <label htmlFor="phoneMobile" className="label">
                  Phone/Mobile: <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="phoneMobile"
                  name="phoneMobile"
                  value={formData.phoneMobile}
                  onChange={handleChange}
                  className="input-field"
                  required
                />
              </div>

              {/* === DATE/TIME LOGIC START === */}
              <div>
                <label htmlFor="admissionDate" className="label">
                  Admission Date:
                </label>
                <input
                  type="date"
                  id="admissionDate"
                  name="admissionDate"
                  value={formData.admissionDate}
                  onChange={handleChange}
                  className={`input-field ${
                    !canEditDate ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                  readOnly={!canEditDate}
                />
              </div>
              <div>
                <label htmlFor="admissionTime" className="label">
                  Admission Time:
                </label>
                <input
                  type="text"
                  id="admissionTime"
                  name="admissionTime"
                  value={formData.admissionTime}
                  onChange={handleChange}
                  className={`input-field ${
                    !canEditDate ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                  readOnly={!canEditDate}
                />
              </div>

              {/* Checkbox for Non-Admins */}
              {!canEditDate && (
                <div className="md:col-span-2 flex items-center gap-2 mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <input
                    type="checkbox"
                    id="isDateChangeRequested"
                    name="isDateChangeRequested"
                    checked={formData.isDateChangeRequested}
                    onChange={handleChange}
                    className="w-4 h-4 text-M-primary-color border-gray-300 rounded focus:ring-M-primary-color"
                  />
                  <label
                    htmlFor="isDateChangeRequested"
                    className="text-sm text-gray-700 font-medium cursor-pointer"
                  >
                    Request Date/Time Correction (Backdate)
                    <span className="block text-xs text-gray-500 font-normal">
                      Check this to notify Admin that the admission date needs
                      adjustment.
                    </span>
                  </label>
                </div>
              )}

              {/* Warning for Admins */}
              {canEditDate && formData.isDateChangeRequested && (
                <div className="md:col-span-2 p-3 bg-red-100 text-red-700 text-sm font-bold rounded border border-red-200 flex flex-col gap-2">
                  <span>
                    ⚠️ This record has a Backdate Request. Please verify and
                    update the Date/Time.
                  </span>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="clearFlag"
                      // Unchecking this means we clear the request (set to false)
                      checked={!formData.isDateChangeRequested}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          isDateChangeRequested: !e.target.checked,
                        }))
                      }
                      className="w-4 h-4"
                    />
                    <label
                      htmlFor="clearFlag"
                      className="font-normal text-black cursor-pointer"
                    >
                      Mark Request as Resolved (Uncheck "Request Active")
                    </label>
                  </div>
                </div>
              )}
              {/* === DATE/TIME LOGIC END === */}

              <div>
                <label htmlFor="relativeMobileNo" className="label">
                  Relative Mobile No:
                </label>
                <input
                  type="text"
                  id="relativeMobileNo"
                  name="relativeMobileNo"
                  value={formData.relativeMobileNo}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="relationship" className="label">
                  Relationship:
                </label>
                <input
                  type="text"
                  id="relationship"
                  name="relationship"
                  value={formData.relationship}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
            </div>
          </div>

          <div className="bg-white shadow-lg rounded-lg">
            <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Others
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
              {/* Consultant */}
              <div>
                <label htmlFor="consultantCode" className="label">
                  Consultant Name: <span className="text-red-600">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <DynamicSelect
                      options={consultantOptions}
                      value={selectedConsultant}
                      onChange={(option) =>
                        handleSelectChange("consultantCode", option)
                      }
                      isSearchable
                      placeholder="Search Consultant"
                      isDisabled={isLoading}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Agent */}
              <div>
                <label htmlFor="agent" className="label">
                  Agent: <span className="text-red-600">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <DynamicSelect
                      options={agentOptions}
                      value={selectedAgent}
                      onChange={(option) => handleSelectChange("agent", option)}
                      isSearchable
                      placeholder="Search Agent"
                      isDisabled={isLoading}
                      required
                    />
                  </div>
                  <button
                    onClick={handleAddNewAgent}
                    className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0 text-sm"
                    disabled={isLoading}
                  >
                    Add New
                  </button>
                </div>
              </div>

              {/* Bed No */}
              <div>
                <label htmlFor="bedNo" className="label">
                  Bed No: <span className="text-red-600">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <DynamicSelect
                      options={bedOptions}
                      value={selectedBed}
                      onChange={(option) => handleSelectChange("bedNo", option)}
                      isSearchable
                      placeholder="Search Bed"
                      isDisabled={isLoading}
                      required
                    />
                  </div>
                  <button
                    onClick={handleAddNewBed}
                    className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0 text-sm"
                    disabled={isLoading}
                  >
                    Add New
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="employeeCode" className="label">
                  Employee Code:
                </label>
                <input
                  type="text"
                  id="employeeCode"
                  name="employeeCode"
                  value={formData.employeeCode}
                  onChange={handleChange}
                  className="input-field bg-gray-100"
                  disabled
                />
              </div>
              <div>
                <label htmlFor="employeeName" className="label">
                  Employee Name:
                </label>
                <input
                  type="text"
                  id="employeeName"
                  name="employeeName"
                  value={formData.employeeName}
                  onChange={handleChange}
                  className="input-field bg-gray-100"
                  disabled
                />
              </div>
              <div>
                <label htmlFor="dischargeCode" className="label">
                  Discharge Code:
                </label>
                <input
                  type="text"
                  id="dischargeCode"
                  name="dischargeCode"
                  value={formData.dischargeCode}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="billingType" className="label">
                  Billing Type:
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <DynamicSelect
                      options={billingTypeOptions}
                      value={selectedBillingType}
                      onChange={(option) =>
                        handleSelectChange("billingType", option)
                      }
                      isSearchable
                      placeholder="Search Billing Type"
                      isDisabled={isLoading}
                    />
                  </div>
                </div>
              </div>
              <div>
                <label htmlFor="status" className="label">
                  Status:
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <DynamicSelect
                      options={statusOptions}
                      value={selectedStatus}
                      onChange={(option) =>
                        handleSelectChange("status", option)
                      }
                      isSearchable
                      placeholder="Select Status"
                      isDisabled={isLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-4">
          <button
            onClick={resetForm}
            className="bg-gray-600 text-xs md:text-sm text-white py-2 px-4 sm:px-6 rounded-md hover:bg-gray-800 transition-all duration-300"
            disabled={isLoading}
          >
            Reset
          </button>
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-xs md:text-sm text-white py-2 px-4 sm:px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveAndPrint}
            className="bg-M-primary-color/90 text-xs md:text-sm text-white py-2 px-4 sm:px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={isLoading}
          >
            {loadingAction === 'print' 
              ? "Saving..." 
              : (isEditMode ? "Update & Print" : "Save & Print")
            }
          </button>
          <button
            type="submit"
            className="bg-M-primary-color/90 text-xs md:text-sm text-white py-2 px-4 sm:px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={isLoading}
          >
            {loadingAction === 'save' 
              ? "Saving..." 
              : (isEditMode ? "Update" : "Save")
            }
          </button>
        </div>
      </form>

      {showPrintPreview && printData && (
        <div className="hospital-print-preview">
          <PrintBillLayout
            className="hospital-print-preview"
            title="Hospital Admission Form"
            billInfo={printData.billInfo}
          />
        </div>
      )}
      <HospitalServiceModal
        isOpen={isNewServiceModalOpen}
        onClose={() => setIsNewServiceModalOpen(false)}
        onSave={handleSaveNewService}
        serviceToEdit={null}
      />
      <HospitalAgentModal
        isOpen={isNewAgentModalOpen}
        onClose={() => setIsNewAgentModalOpen(false)}
        onSave={handleSaveNewAgent}
        agentToEdit={null}
      />
      <HospitalBedModal
        isOpen={isNewBedModalOpen}
        onClose={() => setIsNewBedModalOpen(false)}
        onSave={handleSaveNewBed}
        bedToEdit={null}
      />
    </div>
  );
};

export default HospitalAdmission;